/**
 * Navigation links for the app header.
 *
 * @constant
 * @type {Array<Object>}
 * @property {string} title - The title of the link.
 * @property {string} to - The path to navigate to.
 */
export const headerLinks = [
    { title: 'Tasks', to: '/tasks' },
    { title: 'Missions', to: '/mission-requests/my' },
    { title: 'Notes', to: '/notes' },
    { title: 'New SC', to: '/newsc' },
    { title: 'Admin', to: '/admin' },
    { title: 'HIPAA', to: '/hipaa' },
    { title: 'Reporting', to: '/reporting' },
    { title: 'Install Fails', to: '/installfails' },
    { title: 'TSplus', to: '/licenses' },
    { title: 'Users', to: '/users' },
    { title: 'Logout', to: '/logout' }
];

/**
 * Navigation links for the 'New SC' section.
 *
 * @constant
 * @type {Array<Object>}
 * @property {string} title - The title of the link.
 * @property {string} to - The path to navigate to.
 */
export const newscLinks = [
    {
        title: 'Add New SC',
        to: '/addSC'
    },
    {
        title: 'Send Orientation Complete Email',
        to: '/completeOrientation'
    }
];

export const scLinks = [
    {
        title: 'Dashboard',
        to: '/scDashboard'
    },
    {
        title: 'Fishbowl',
        to: '/fishbowl'
    },
    {
        title: 'SC Vetting Cards',
        to: '/scCards'
    }
];

/**
 * Navigation links for the 'Admin' section.
 *
 * @constant
 * @type {Array<Object>}
 * @property {string} title - The title of the link.
 * @property {string} to - The path to navigate to.
 */
export const adminLinks = [
    {
        title: 'OAC',
        to: '/oac'
    },
    {
        title: 'OAR',
        to: '/oar'
    },
    {
        title: 'User Removal',
        to: '/userRemoval'
    },
    {
        title: 'AOAR',
        to: '/aoar'
    },
    {
        title: 'Email Jobs',
        to: '/emailJobs'
    },
    {
        title: 'Computer System Requirement',
        to: '/csr'
    },
    { title: 'BP Removal', to: '/bpRemoval' }
];

/**
 * Navigation links for the 'HIPAA' section.
 *
 * @constant
 * @type {Array<Object>}
 * @property {string} title - The title of the link.
 * @property {string} to - The path to navigate to.
 */
export const hipaaLinks = [
    {
        title: 'Concerns',
        to: '/hipaaconcerns'
    },
    {
        title: 'Corrections',
        to: '/hipaacorrections'
    },
    {
        title: 'Send OS Fail Email',
        to: '/hipaaOsEmail'
    }
];

/**
 * Navigation links for the 'User Management' section.
 *
 * @constant
 * @type {Array<Object>}
 * @property {string} title - The title of the link.
 * @property {string} to - The path to navigate to.
 */
export const userManagementLinks = [
    {
        title: 'Create a User',
        to: '/createuser'
    },
    {
        title: 'Update a User',
        to: '/updateuser'
    },
    {
        title: 'Delete a User',
        to: '/deleteuser'
    }
];

const reportingExecutivePulseLink = {
    title: 'Executive Pulse',
    to: '/executive-dashboard'
};

const reportingLinksWithoutExecutivePulse = [
    {
        title: 'Home',
        to: '/reporting'
    },
    {
        title: 'HD Weekly Stats',
        to: '/hdweeklystats'
    },
    {
        title: 'New SC/Admin Weekly Stats',
        to: '/newscweeklystats'
    },
    {
        title: 'NOI Weekly Stats',
        to: '/ncltweeklystats'
    },
    {
        title: 'Copilot Weekly Stats',
        to: '/copilotWeeklyReport'
    },
    {
        title: 'Query Tool',
        to: '/queryTool'
    },
    {
        title: 'Audit Tickets',
        to: '/auditTickets'
    },
    {
        title: 'TSplus Licenses',
        to: '/licenseReporting'
    }
];

/**
 * Reporting sub-navigation. "Executive Pulse" is shown only when Oyster grants
 * `executivePulseAccess` (see `useCurrentUser`).
 *
 * @param {boolean} includeExecutivePulse
 * @returns {Array<{ title: string, to: string }>}
 */
export function getReportingLinks(includeExecutivePulse) {
    if (includeExecutivePulse) {
        return [
            reportingExecutivePulseLink,
            ...reportingLinksWithoutExecutivePulse
        ];
    }
    return reportingLinksWithoutExecutivePulse;
}

/**
 * Reporting nav links for the current user (hides Audit Tickets for non-leads).
 *
 * @param {{ loading?: boolean, canAccessWeeklyAudit?: boolean, executivePulseAccess?: boolean }} ctx
 * @returns {Array<{ title: string, to: string }>}
 */
export function reportingLinksForUser(ctx = {}) {
    const base = getReportingLinks(ctx.executivePulseAccess);
    if (ctx.loading || ctx.canAccessWeeklyAudit) {
        return base;
    }
    return base.filter((link) => link.to !== '/auditTickets');
}
